import { Button, ConfirmDialog, FileUpload, FileUploadArea, IconButton, NewFileUploadArea, StandardDialog, VerticalBarLoader } from '@roc/ui';
import { observer } from 'mobx-react';
import { routeByLoanSubType, useLoanStore, useLoanSubmissionRoutes } from '@roc/feature-loans';
import { DraftLoan, Document as DocumentType } from '@roc/feature-types';
import { useHistory } from 'react-router';
import { LoanProcess, LoanStatus, LoanSubType, UNDERWRITING } from '@roc/feature-utils';
import { TodoItem } from '../utils/loanDetailsTypes';
import { Box, CircularProgress, createStyles, Grid, makeStyles, Theme, Tooltip, Typography } from '@material-ui/core';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import { DocumentName, useDocumentStore } from '@roc/feature-documents';
import { Document, Page } from 'react-pdf/dist/esm/entry.webpack';
import { TaskStatus, useGeneralTasksStore } from '@roc/feature-general-tasks';
import { ArrowDownward, ChatBubble, Delete, ArrowForward, GetApp, InsertDriveFile, Error, Forum, ForumOutlined } from '@material-ui/icons';
import { BannerMessages } from 'libs/feature-loans/src/loans/components/bannerMessages';
import { ChatSection, FilterType, MyChatContainer, SelectConversation, SimpleListSkeleton, useLoanCommunicationStore } from '@roc/feature-communication';
import { ChatContainer } from '@chatscope/chat-ui-kit-react';
import { useStore } from '@roc/client-portal-shared/hooks';



interface Props {
  document: DocumentType;
  expand: boolean;
  customTopContent?: ReactNode;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    document: {
      width: 'fit-content',
      margin: '0 auto',
    },
    page: {
      margin: theme.spacing(2),
      boxShadow: '2px 2px 4px #888',
      width: 'fit-content',
    },
    chatContainer: {
      flex: 1,
      height: ''
    },
    createChatButton: {
      letterSpacing: 'normal',
      textTransform: 'none',
      fontWeight: 'normal',
      fontSize: '16px',
      backgroundColor: '#4285F4',
      width: '307px',
      height: '64px',
      padding: '8px 16px',
      boxShadow: '0px 4px 4px 0px #00000040',
      '&:hover': {
        backgroundColor: '#357AE8',
      },
    },
    createChatButtonContainer: {
      height: '75vh',
      border: '1px solid #CBCBCB',
      borderRadius: '10px',
      padding: '8px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexDirection: 'column'
    },
    circleIconContainer: {
      width: '44px',
      height: '44px',
      borderRadius: '50%',
      backgroundColor: '#FFFFFF',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginRight: '5px',
    }
  })
);

export const LineItemUpload = observer(({
  document, expand, customTopContent
}: Props) => {
  const classes = useStyles();
  const { generalTasksStore } = useGeneralTasksStore();
  const { loanCommunicationStore } = useLoanCommunicationStore();
  const { documentStore } = useDocumentStore();
  const [scale, setScale] = useState(1.0);
  const [uploads, setUploads] = useState<FileUpload[]>([]);
  const [numPages, setNumPages] = useState(null);
  const [showLoading, setShowLoading] = useState<boolean>(false);
  const [showDocumentLoading, setShowDocumentLoading] = useState<boolean>(false);
  const [isPdfFile, setIsPdfFile] = useState<boolean>(false);
  const [canUploadFile] = useState<boolean>(document.canUploadFile);
  const [hasClosingCondition, setHasClosingCondition] = useState<boolean>(document.closingCondition && document.status !== TaskStatus.ACCEPTED);
  const [isCogsOnline, setIsCogsOnline] = useState(false);


  function onDocumentLoadSuccess({ numPages }) {
    setNumPages(numPages);
  }

  useEffect(() => {
    syncUploads(document)
    checkIfPdf()
    if (document.canDownloadFile) {
      documentStore.previewDocumentV2(document);
    }

    return () => {
      loanCommunicationStore.reset();
    };
  }, [])

  useEffect(() => {
    if (uploads[0] && uploads[0].file != null) {
      setShowDocumentLoading(true);
      documentStore.uploadDocument(uploads[0].file, document, UNDERWRITING, () => {
        documentStore.previewDocumentV2(document);
        setShowDocumentLoading(false);
        checkIfPdf();
      });
    }
  }, [uploads])

  useEffect(() => {
    if (!expand) {
      generalTasksStore.checkIfTaskRoomExist(document.taskId, () => loadChat())
    }
  }, [expand])

  const loadChat = () => {
    setShowLoading(true)
    loanCommunicationStore.reset();
    loanCommunicationStore.setFilter({
      type: FilterType.TASK_ID,
      value: document.taskId
    });
    if (loanCommunicationStore.isInternal) {
      loanCommunicationStore.getAllMessageSnippets();
    }
    loanCommunicationStore.loadMyConversations(1, 100, (totalCount, rows) => {
      setShowLoading(false);
      if (rows[0]) {
        loanCommunicationStore.setCurrentConversation(loanCommunicationStore.conversations[rows[0].conversationSid])
      }
    }, () => {
      setShowLoading(false);
    });
  }

  const createChat = (taskId) => {
    generalTasksStore.createTaskChatroom(taskId, () => {
      loadChat()
    });
  }

  const checkIfPdf = () => {
    if (uploads[0]) {
      if (uploads[0].name.split('.')[1] === 'pdf') {
        setIsPdfFile(true)
      } else {
        setIsPdfFile(false)
      }
    } else {
      if (document.docType === 'pdf') {
        setIsPdfFile(true)
      } else {
        setIsPdfFile(false)
      }
    }
  }

  const syncUploads = (document: DocumentType) => {
    if (document.canDownloadFile) {
      const newItems = {
        name: document.originalFileName,
        file: null,
        type: document.docType
      };
      setUploads([newItems]);
    }
  }

  return (
    <>
      <Grid container spacing={2} alignItems="stretch" style={{ paddingTop: '0px' }}>
        {customTopContent && (<Grid item xs={12} style={{ paddingLeft: 16, transition: 'all 0.3s ease' }}>
          {customTopContent}
        </Grid>)}
        <Grid item xs={12} style={{ padding: 16, transition: 'all 0.3s ease' }}>
          {canUploadFile && (
            <Box style={{
              marginBottom: uploads[0] ? 16 : 0,
              transition: 'all 0.5s ease',
            }}>
              <NewFileUploadArea
                items={uploads}
                setItems={(items) => setUploads(items)}
                itemDocument={document}
                showUploadButton={true}
                multiple={false}
                fileTypes={document.taskName === DocumentName.TAKE_INTERNAL_VALUATION_PHOTOS_APPRAISAL ? ['pdf'] : undefined}
              />
            </Box>
          )}
          {documentStore.documentPreviewV2 && uploads[0] &&
            <>
              {isPdfFile ?
                <Box style={{
                  height: hasClosingCondition ? '55vh' : '67.9vh',
                  overflowY: 'auto',
                  border: '1px solid #CBCBCB',
                  borderRadius: '10px',
                  padding: '8px',
                  display: showDocumentLoading ? 'flex' : 'block',
                  justifyContent: 'center',
                  alignItems: 'center',
                  transition: 'all 0.5s ease',
                }}>
                  {showDocumentLoading ? <CircularProgress size={32} /> :
                    <Document
                      file={documentStore.documentPreviewV2.data}
                      onLoadSuccess={onDocumentLoadSuccess}
                      className={classes.document}
                    >
                      {numPages &&
                        [...Array(numPages)].map((x, i) => {
                          return (
                            <Page
                              className={classes.page}
                              pageNumber={i + 1}
                              scale={scale}
                            />
                          );
                        })}
                    </Document>
                  }
                </Box>
                :
                <Box display="flex" alignItems="center" justifyContent="center" flexDirection="column" style={{
                  height: hasClosingCondition ? '55vh' : '67.9vh',
                  overflowY: 'auto',
                  border: '1px solid #CBCBCB',
                  borderRadius: '10px',
                  padding: '8px',
                  transition: 'all 0.5s ease',
                }}>
                  <Error style={{ marginBottom: '5px', fontSize: '40px' }}></Error>
                  <Typography variant="h6" align='center'>
                    We are currently unable to show a preview of the document
                  </Typography>
                </Box>
              }
            </>
          }

          {hasClosingCondition &&
            <BannerMessages
              isInlineItem={true}
              messages={[
                {
                  title: 'This line item must be completed for the loan to be funded.',
                  content: "Your loan will not be funded until this item is resolved.",
                },
              ]}
            />
          }
        </Grid>
      </Grid>
    </>
  );
});
